import {
  differenceInDays,
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  intervalToDuration,
  parseISO,
} from 'date-fns';
import { isEqual } from 'lodash-es';
import OneSignal from 'react-onesignal';
import { getClientId } from 'Shared/ga';
import { trackMP, updateUserOnMP } from 'Shared/mp';

import { isFreeUser } from './components/Authorize';
import { updateReferralData } from './redux/actions';
import store from './redux/store';

export const MULTI_FREE_HABITAT_COUNT = 2;

export const secondMs = 1000;
export const minuteMs = secondMs * 60;
export const hourMs = minuteMs * 60;
export const dayMs = hourMs * 24;
export const weekMs = dayMs * 7;

export const isValidUrl = (url) => {
  try {
    return !!new URL(url.startsWith('/') ? `${window.location.origin}${url}` : url);
  } catch {
    return false;
  }
};

export const emailRegex = /^[A-Z0-9+_.-]+@[A-Z0-9.-]+\.[A-Z0-9.-]+$/gi;
// eslint-disable-next-line no-useless-escape
export const passwordRegex = /^(?=.*[a-zA-Z0-9!@#$%^&*()\[\]?,.<>:;'"|\\\/-_+=])(?!.*\s).{8,50}/gm;

export const iOSDevice = () => !!navigator.platform.match(/iPhone|iPod|iPad/);
export const androidDevice = () => !!navigator.userAgent.match(/Android/i);

export const isDev = () => process.env.PREACT_APP_ENVIRONMENT === 'development';
export const getHomeURL = () => (!isDev() ? 'https://zoolife.tv' : 'https://zoolife.live');

export const getDeviceType = () => {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const ua = navigator.userAgent;
  if (
    /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'tablet';
  }

  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'phone';
  }

  return 'desktop';
};

export const isPhone = () => getDeviceType() === 'phone';

export const getDesktopOrMobile = () => {
  const type = getDeviceType();

  if (type === 'tablet' || type === 'phone') {
    return 'mobile';
  }

  return 'desktop';
};

export const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const generateTitle = (part) => (part ? `zoolife - ${part}` : 'zoolife');

export const formatAge = (dateOfBirth) =>
  formatDistanceToNowStrict(parseISO(dateOfBirth), { addSuffix: false, roundingMethod: 'floor' });

export const logPageView = (page) => {
  if (typeof window !== 'undefined') {
    const context = {
      referrer: '',
      page: {
        referrer: '',
      },
    };

    if (window.analytics) {
      if (page) {
        window.analytics.page({
          path: `${page}`,
          url: `${page}`,
          ...context,
        });
      } else {
        window.analytics.page();
      }
    }
  }
};

export const identifyUser = (user) => {
  const { _id: userId, role } = user;
  if (window.analytics) {
    window.analytics.identify(userId, {
      role,
    });
  }
};

const partners = {
  '/oranapark': 'orana',
  '/orana': 'orana',
  '/torontozoo': 'torontozoo',
  '/sbzoo': 'sbzoo',
  '/phoenixherp': 'phoenixherp',
  '/lpks': 'lpks',
  '/lpzoo': 'lpzoo',
  '/northumberlandzoo': 'northumberlandzoo',
  '/akronzoo': 'akronzoo',
};

export const logAndGetCampaignData = async () => {
  try {
    const { referrer } = document;
    const { searchParams, pathname } = new URL(document.location);
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');
    const utmHabitat = searchParams.get('utm_habitat');
    const partner = searchParams.get('partner');
    const referralData = {
      utmSource,
      utmMedium,
      utmCampaign,
      utmHabitat,
      partner,
      referrer,
    };

    // when we move all the partner pages to webflow, we can remove this if statement:
    if (Object.keys(partners).includes(pathname)) {
      referralData.partner = partners[pathname];
    }

    const storedReferralData = JSON.parse(localStorage.getItem('referralData')) ?? {};
    const {
      utmSource: localUTMSource,
      utmMedium: localUTMMedium,
      utmCampaign: localUTMCampaign,
      utmHabitat: localUTMHabitat,
      referrer: localReferrerCampaign,
      partner: localPartner,
    } = storedReferralData;

    if (!isEqual(storedReferralData, referralData)) {
      let clientId = null;
      try {
        clientId = await getClientId();
      } catch (err) {
        console.error('GA is not able to create client ID', err);
      }
      const finalRefData = {
        utmSource: localUTMSource || utmSource,
        utmMedium: localUTMMedium || utmMedium,
        utmCampaign: localUTMCampaign || utmCampaign,
        utmHabitat: utmHabitat || localUTMHabitat,
        // Preserve referrer if referrer is coming from Google OAuth
        referrer: referrer?.indexOf('accounts.google') >= 0 ? localReferrerCampaign : localReferrerCampaign || referrer,
        partner: localPartner || referralData.partner,
        gaClientID: clientId,
      };
      localStorage.setItem('referralData', JSON.stringify({ ...storedReferralData, ...finalRefData }));
      return { ...storedReferralData, ...finalRefData };
    }
    return storedReferralData;
  } catch (err) {
    console.error('Error setting up referral data on local storage', err);
    return {};
  }
};

export const getCampaignData = () => {
  try {
    return JSON.parse(localStorage.getItem('referralData')) || {};
  } catch (err) {
    console.error('Error getting referral data on local storage', err);
    return {};
  }
};

export const setLearnedAboutUsRefData = (learnedAboutUs) => {
  try {
    const currentRefData = JSON.parse(localStorage.getItem('referralData')) || {};
    localStorage.setItem('referralData', JSON.stringify({ ...currentRefData, learnedAboutUs }));
  } catch (err) {
    console.error('Error setting learnedAboutUs referral data on local storage', err);
  }
};

// the _gaexp cookie won't be created until user see the page which the experiment is launched
// there. so we need to call this function on each component we launch the experiment.
export const storeExperimentInReferralData = async () => {
  const parseExperimentCookie = () =>
    new Promise((resolve, reject) => {
      const experiments = {};
      const gaExpCookie = document.cookie.match(/_gaexp=([^;]+)/);
      if (gaExpCookie) {
        const parts = gaExpCookie[1].replaceAll('!', '.').split('.');
        if (parts.length > 3 && parts[0].includes('GAX')) {
          parts.splice(0, 2);
          for (let i = 0; i < parts.length / 3; i += 1) {
            const experimentId = parts[0 + i * 3];
            const variantId = parts[2 + i * 3];
            experiments[experimentId] = variantId;
          }
          resolve(experiments);
        } else {
          reject(new Error(`the _gaexp cookie format is incompatible: ${gaExpCookie[1]}`));
        }
      } else {
        reject(new Error('the _gaexp cookie not found.'));
      }
    });
  try {
    const allExperiments = await parseExperimentCookie();
    if (Object.keys(allExperiments).length > 0) {
      const currentRefData = JSON.parse(localStorage.getItem('referralData')) || {};
      const updatedRefData = {
        ...currentRefData,
        experiments: allExperiments,
      };
      localStorage.setItem('referralData', JSON.stringify(updatedRefData));
      store.dispatch(updateReferralData(updatedRefData));
      updateUserOnMP(store.getState()?.user);
    }
  } catch (err) {
    console.error('Error storing experiment data :', err);
  }
};

export const getConfig = (configs = [], key) => configs.find(({ configKey }) => configKey === key) || {};

export const loadPage = (path, keepQueryString) => {
  if (window.iframeContext) {
    window.open(
      `${window.location.origin}${path}${keepQueryString ? window.location.search : ''}`,
      window.iframeContext.linkTarget,
    );
  } else {
    window.location.href = `${path}${keepQueryString ? window.location.search : ''}`;
  }
};

export const formatSecondsToVideoDuration = (timeInSeconds) => {
  const { hours, minutes, seconds } = intervalToDuration({ start: 0, end: timeInSeconds * 1000 });
  return `${hours > 0 ? `${`0${hours}`.slice(-2)}:` : ''}${`0${minutes}`.slice(-2)}:${`0${seconds}`.slice(-2)}`;
};

export const isHabitatUnlocked = (userSubscription, habitatId) => {
  const { isFreemium, freeHabitats } = userSubscription;
  return !isFreemium || freeHabitats?.includes(habitatId);
};

export const notificationPermissionStatus = () =>
  new Promise((resolve) => {
    try {
      const timeout = setTimeout(() => resolve(''), 1000);
      OneSignal.getNotificationPermission().then((res) => {
        clearTimeout(timeout);
        resolve(res);
      });
    } catch (e) {
      resolve('');
    }
  });

export const requestNotificationPermission = async () => {
  await OneSignal.showNativePrompt();
};

export const isLandscape = () =>
  typeof window !== 'undefined' ? window.matchMedia('(orientation:landscape)').matches : null;
export const isPortrait = () => (typeof window !== 'undefined' ? window.matchMedia('(orientation:portrait)').matches : null);

export const msToReadable = (millis) => {
  const hour = Math.floor(millis / hourMs);
  const minutes = Math.floor((millis % hourMs) / minuteMs);
  const seconds = ((millis % minuteMs) / secondMs).toFixed(0);
  return `${hour > 0 ? `${hour}:` : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

// this function is not in use yet, mainly to define/unify who can see what logic
export const canAccessHabitat = (role, validUntil, habitatId, freeHabitats) => {
  // Admins, Partners, Hosts should have access to all habitats
  if (['admin', 'partner', 'host'].includes(role)) {
    return true;
  }

  // Free habitats should be accessible even after subscription is done
  if (freeHabitats.includes(habitatId)) {
    return true;
  }

  // if not free habitat, and validUntil is not in the future
  if (['vip', 'user'].includes(role) && validUntil?.getTime() >= Date.now()) {
    return true;
  }

  return false;
};

// Log events to Facebook
export const trackFB = (action, event) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(action, event);
  }
};

export const copyToClipboard = (text, onChange = () => {}) => {
  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => onChange(true))
      .catch((err) => console.error('Error while copying via navigator.clipboard', err));
  } else {
    // if clipboard is not supported, attempt to use old alternative (deprecated)
    console.warn('clipboard is not supported, copy via execCommand.');
    const elem = document.createElement('input');
    elem.value = text;
    document.body.appendChild(elem);
    elem.select();

    try {
      document.execCommand('copy');
      onChange(true);
    } catch (err) {
      console.error('Error while copying to clipboard', err);
    } finally {
      document.body.removeChild(elem);
    }
  }
};

export const setLocalStorageWithODTP = (subscription) => {
  if (subscription) {
    try {
      if (localStorage.getItem('odtp')) {
        return;
      }
      if (
        ['tz-bundle-membership-offline-day-ticket', 'tz-bundle-membership-online-day-ticket'].includes(
          subscription.productName,
        )
      ) {
        const odtpValue = {
          productId: subscription.productId,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('odtp', JSON.stringify(odtpValue));
      }
    } catch (error) {
      console.error('Unable to access localStorage:', error);
    }
  }
};

export const checkLocalStorageForODTP = (odtpParam) => {
  try {
    if (odtpParam) {
      if (!isFreeUser()) {
        trackMP('upgrade-to-ODTP-rejected', {
          reason: 'already has an active subscription',
          rejectionPlace: 'front-end',
        });
        return null;
      }
      const storedValue = localStorage.getItem('odtp');
      if (storedValue) {
        // const parsedValue = JSON.parse(storedValue);
        // we have access to parsedValue.productId and parsedValue.timestamp for the future use
        trackMP('upgrade-to-ODTP-rejected', {
          reason: 'already used ODTP based on localstorage record',
          rejectionPlace: 'front-end',
        });
        return null;
      }
      return odtpParam;
    }
    return null;
  } catch (error) {
    console.error('Unable to access localStorage:', error);
    return odtpParam;
  }
};

// this function will format a date to either 'x ago' format or a formatted timestamp:

export const formatTimeAgoOrTimestamp = (date, shortFormat = false) => {
  const formatDistanceToNowStrictLocale = {
    xSeconds: '{{count}}s',
    xMinutes: '{{count}}m',
    xHours: '{{count}}h',
  };
  const shortEnLocale = {
    formatDistance: (token, count) => formatDistanceToNowStrictLocale[token].replace('{{count}}', count),
  };

  if (differenceInDays(new Date(), date) < 1) {
    if (shortFormat) {
      return `${formatDistanceToNowStrict(date, { addSuffix: false, locale: shortEnLocale })} ago`;
    }
    return `${formatDistanceToNow(date, { addSuffix: true })}`;
  }

  return format(date, 'dd-MM-yy hh:mma');
};
